/**
 * Chart modules - the loader.
 *
 * This file is tiny and ships in the main bundle. The charting library is not:
 * Chart.js is 62KB gzipped, a chart appears on a minority of blog posts, and
 * it appears on no other page on the site at all. The homepage is the page
 * that has to be fast, so it must not pay for this.
 *
 * `import()` is what buys that. One build, one source tree, one `npm run
 * build` - and Vite splits charts-render.js and Chart.js into a separate chunk
 * that is fetched only when a chart is genuinely about to come into view.
 *
 * Every failure path here ends in revealTable(), which un-hides the data table
 * the Twig module already rendered. A reader whose JavaScript never arrives,
 * or whose browser is too old, or whose network dropped the chunk, gets the
 * numbers as a table rather than a blank space where an argument should be.
 */

const SELECTOR = "canvas.chart-module__canvas[data-chart]";

/**
 * Show the figures as a table instead of a chart.
 *
 * The table is always in the HTML - see chartModule.twig for why - so this is
 * a class change rather than a render.
 */
function revealTable(canvas) {
  const figure = canvas.closest(".chart-module__figure");
  const table = figure && figure.querySelector(".chart-module__data");

  if (table) {
    table.classList.remove("visually-hidden");
  }

  // Take the reserved space with it, or the table sits below an empty
  // 16:9 hole.
  const frame = canvas.closest(".chart-module__frame");
  (frame || canvas).remove();
}

/**
 * Draws the charts in `root` as they come into view. Returns a teardown that
 * stops watching and destroys the charts drawn so far (each holds a resize
 * observer and an animation loop of its own), for the page lifecycle.
 */
export function initCharts(root) {
  const canvases = root.querySelectorAll(SELECTOR);

  if (!canvases.length) {
    return;
  }

  // No IntersectionObserver means a browser old enough that the table is the
  // better experience anyway. Fall back rather than polyfill.
  if (!("IntersectionObserver" in window)) {
    canvases.forEach(revealTable);
    return;
  }

  let live = true;
  const charts = [];
  let pending = null;
  const load = () => {
    pending = pending || import("./charts-render.js");
    return pending;
  };

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        // Unobserve first. A chunk that fails to load must not be retried on
        // every scroll event.
        obs.unobserve(entry.target);

        load()
          .then(({ renderChart }) => {
            if (live) charts.push(renderChart(entry.target));
          })
          .catch(() => revealTable(entry.target));
      });
    },
    // Start fetching before the chart is on screen, so the common case is a
    // chart that is already drawn by the time it is scrolled to.
    { rootMargin: "300px 0px" },
  );

  canvases.forEach((canvas) => observer.observe(canvas));

  return () => {
    live = false;
    observer.disconnect();
    charts.forEach((chart) => chart.destroy());
  };
}
